import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Assunto, Departamento } from '../../types';
import { Plus, Search, Edit, Trash2, Eye, Filter, BookOpen, Building2, Calendar } from 'lucide-react';
import { AssuntosService, DepartamentosService } from '../../services';
import toast from 'react-hot-toast';
import ConfirmationModal from '../../components/ConfirmationModal';
import Modal from '../../components/ui/Modal';



interface Filters {
  departamento: string;
}

interface Pagination {
  currentPage: number;
  itemsPerPage: number;
  totalItems: number;
  totalPages: number;
}

interface ModalData {
  isOpen: boolean;
  mode: 'create' | 'edit';
  subject?: Assunto;
}

const Subjects: React.FC = () => {
  const { usuario } = useAuth();
  const navigate = useNavigate();
  const [subjects, setSubjects] = useState<Assunto[]>([]);
  const [departments, setDepartments] = useState<Departamento[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [filters, setFilters] = useState<Filters>({
    departamento: 'all'
  });
  const [pagination, setPagination] = useState<Pagination>({
    currentPage: 1,
    itemsPerPage: 10,
    totalItems: 0,
    totalPages: 0
  });
  const [modalData, setModalData] = useState<ModalData>({
    isOpen: false,
    mode: 'create'
  });
  const [formData, setFormData] = useState({
    nome: '',
    descricao: '',
    departamento_id: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    subjectId: 0,
    subjectName: ''
  });

  // Verificar se o usuário tem acesso
  useEffect(() => {
    if (usuario && usuario.nivel === 'usuario') {
      navigate('/dashboard');
      toast.error('Acesso negado. Apenas administradores e desenvolvedores podem acessar esta página.');
      return;
    }

    if (usuario) {
      loadSubjects();
      loadDepartments();
    }
  }, [usuario, navigate]);

  const loadSubjects = async (): Promise<void> => {
    try {
      setIsLoading(true);
      const response = await AssuntosService.getAssuntos();
      if (Array.isArray(response)) {
        setSubjects(response);
      } else {
        toast.error('Erro ao carregar assuntos');
        setSubjects([]);
      }
    } catch (error: any) {
      console.error('Erro ao carregar assuntos:', error);
      if (error.message?.includes('Acesso negado')) {
        toast.error('Você não tem permissão para acessar esta página');
        navigate('/dashboard');
      } else {
        toast.error('Erro ao carregar assuntos');
      }
      setSubjects([]);
    } finally {
      setIsLoading(false);
    }
  };

  const loadDepartments = async (): Promise<void> => {
    try {
      const response = await DepartamentosService.getDepartamentos();
      if (Array.isArray(response)) {
        // Se for admin, mostrar apenas os departamentos dele
        if (usuario?.nivel === 'admin' && usuario.departamentos && usuario.departamentos.length > 0) {
          // Filtrar apenas os departamentos do usuário admin
          const userDepts = response.filter(d => 
            usuario.departamentos.some(userDept => userDept.id === d.id)
          );
          setDepartments(userDepts);
        } else {
          setDepartments(response);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar departamentos:', error);
    }
  };

  const openDeleteModal = (subjectId: number, subjectName: string) => {
    setDeleteModal({
      isOpen: true,
      subjectId,
      subjectName
    });
  };

  const closeDeleteModal = () => {
    setDeleteModal({
      isOpen: false,
      subjectId: 0,
      subjectName: ''
    });
  };

  const confirmDelete = async () => {
    try {
      await AssuntosService.deleteAssunto(deleteModal.subjectId);
      toast.success('Assunto excluído com sucesso');
      loadSubjects(); // Recarregar lista
      closeDeleteModal();
    } catch (error: any) {
      console.error('Erro ao excluir assunto:', error);
      if (error.message?.includes('Acesso negado')) {
        toast.error('Você não tem permissão para excluir este assunto');
      } else {
        toast.error('Erro ao excluir assunto');
      }
    }
  };

  const filteredSubjects = subjects.filter(subject => {
    const matchesSearch = subject.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      subject.descricao?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesDepartamento = filters.departamento === 'all' ||
      subject.departamento_id?.toString() === filters.departamento;

    // Remover filtro de status já que não temos mais campo ativo
    return matchesSearch && matchesDepartamento;
  });

  // Calcular paginação
  const totalItems = filteredSubjects.length;
  const totalPages = Math.ceil(totalItems / pagination.itemsPerPage);

  // Atualizar paginação quando os filtros mudarem
  useEffect(() => {
    setPagination(prev => ({
      ...prev,
      currentPage: 1,
      totalItems,
      totalPages
    }));
  }, [totalItems, totalPages]);

  // Obter itens da página atual
  const getCurrentPageItems = () => {
    const startIndex = (pagination.currentPage - 1) * pagination.itemsPerPage;
    const endIndex = startIndex + pagination.itemsPerPage;
    return filteredSubjects.slice(startIndex, endIndex);
  };

  const handlePageChange = (page: number) => {
    setPagination(prev => ({
      ...prev,
      currentPage: page
    }));
  };

  const openCreateModal = () => {
    setModalData({ isOpen: true, mode: 'create' });
    setFormData({
      nome: '',
      descricao: '',
      departamento_id: usuario?.nivel === 'admin' && usuario.departamentos && usuario.departamentos.length > 0 ? usuario.departamentos[0].id.toString() : ''
    });
  };

  const openEditModal = (subject: Assunto) => {
    setModalData({ isOpen: true, mode: 'edit', subject });
    setFormData({
      nome: subject.nome || '',
      descricao: subject.descricao || '',
      departamento_id: subject.departamento_id?.toString() || ''
    });
  };

  const closeModal = () => {
    setModalData({ isOpen: false, mode: 'create' });
    setFormData({ nome: '', descricao: '', departamento_id: '' });
    setIsSubmitting(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.nome.trim() || !formData.descricao.trim() || !formData.departamento_id) {
      toast.error('Todos os campos são obrigatórios');
      return;
    }

    setIsSubmitting(true);

    try {
      if (modalData.mode === 'create') {
        await AssuntosService.createAssunto({
          nome: formData.nome.trim(),
          descricao: formData.descricao.trim(),
          departamento_id: parseInt(formData.departamento_id)
        });
        toast.success('Assunto criado com sucesso!');
      } else {
        if (modalData.subject) {
          await AssuntosService.updateAssunto(modalData.subject.id, {
            nome: formData.nome.trim(),
            descricao: formData.descricao.trim(),
            departamento_id: parseInt(formData.departamento_id)
          });
          toast.success('Assunto atualizado com sucesso!');
        }
      }

      closeModal();
      loadSubjects(); // Recarregar lista
    } catch (error: any) {
      console.error('Erro ao salvar assunto:', error);
      toast.error(error.message || 'Erro ao salvar assunto');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 dark:border-primary-400"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-gray-200/50 dark:border-gray-700/50 shadow-soft">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div className="flex-1">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              Assuntos dos Tickets
            </h1>
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
              {usuario?.nivel === 'dev'
                ? 'Gerencie todos os tipos de assuntos para categorização dos tickets'
                : 'Gerencie os tipos de assuntos do seu departamento para categorização dos tickets'
              }
              {usuario?.nivel === 'admin' && usuario.departamentos && usuario.departamentos.length > 0 && (
                <span className="block text-sm text-primary-600 dark:text-primary-400 mt-1">
                  Departamentos: {usuario.departamentos.map(d => d.nome).join(', ')}
                </span>
              )}
            </p>
            <div className="mt-2 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
              Total de assuntos: {subjects.length} • Filtrados: {filteredSubjects.length}
            </div>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
            <button
              onClick={openCreateModal}
              className="btn btn-primary w-full sm:w-auto justify-center"
            >
              <Plus className="w-4 h-4 mr-2" />
              Novo Assunto
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-gray-200/50 dark:border-gray-700/50 shadow-soft">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" size={20} />
            <input
              type="text"
              placeholder="Buscar assuntos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500 dark:focus:border-primary-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 text-sm"
            />
          </div>

          {/* Departamento Filter */}
          <select
            value={filters.departamento}
            onChange={(e) => setFilters({ ...filters, departamento: e.target.value })}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500 dark:focus:border-primary-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
          >
            <option value="all">Todos os departamentos</option>
            {departments.map(department => (
              <option key={department.id} value={department.id}>
                {department.nome}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Subjects Table */}
      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl border border-gray-200/50 dark:border-gray-700/50 shadow-soft overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900/50">
              <tr>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Nome
                </th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Descrição
                </th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Departamento
                </th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {getCurrentPageItems().length > 0 ? (
                getCurrentPageItems().map((subject) => (
                  <tr key={subject.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="p-1.5 sm:p-2 rounded-lg bg-primary-100 dark:bg-primary-900/50 text-primary-600 dark:text-primary-400 mr-2 sm:mr-3">
                          <BookOpen className="w-3 h-3 sm:w-4 sm:h-4" />
                        </div>
                        <div className="text-xs sm:text-sm font-medium text-gray-900 dark:text-gray-100">
                          {subject.nome || 'N/A'}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                      <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 max-w-xs truncate">
                        {subject.descricao || 'N/A'}
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Building2 className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400 dark:text-gray-500 mr-1.5 sm:mr-2" />
                        <span className="text-xs sm:text-sm text-gray-900 dark:text-gray-100">
                          {subject.departamento_nome || departments.find(d => d.id === subject.departamento_id)?.nome || 'N/A'}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm font-medium">
                      <div className="flex items-center space-x-1 sm:space-x-2">
                        <button
                          onClick={() => openEditModal(subject)}
                          className="text-yellow-600 dark:text-yellow-400 hover:text-yellow-900 dark:hover:text-yellow-300 p-1"
                        >
                          <Edit className="w-3 h-3 sm:w-4 sm:h-4" />
                        </button>
                        <button
                          onClick={() => openDeleteModal(subject.id, subject.nome || '')}
                          className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 p-1"
                          title="Excluir assunto"
                        >
                          <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="px-3 sm:px-6 py-8 sm:py-12 text-center text-gray-500 dark:text-gray-400">
                    <BookOpen className="w-8 h-8 sm:w-12 sm:h-12 mx-auto mb-3 sm:mb-4 text-gray-400 dark:text-gray-500" />
                    <p className="text-base sm:text-lg font-medium">Nenhum assunto encontrado</p>
                    <p className="text-xs sm:text-sm">Tente ajustar os filtros de busca ou crie um novo assunto</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-gray-200/50 dark:border-gray-700/50 shadow-soft">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div className="text-xs sm:text-sm text-gray-700 dark:text-gray-300 text-center sm:text-left">
              Mostrando {((pagination.currentPage - 1) * pagination.itemsPerPage) + 1} a {Math.min(pagination.currentPage * pagination.itemsPerPage, totalItems)} de {totalItems} resultados
            </div>
            <div className="flex flex-col sm:flex-row items-center space-y-3 sm:space-y-0 sm:space-x-2">
              {/* Botão Anterior */}
              <button
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={pagination.currentPage === 1}
                className={`px-3 py-2 text-xs sm:text-sm font-medium rounded-lg border w-full sm:w-auto ${pagination.currentPage === 1
                  ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 border-gray-200 dark:border-gray-600 cursor-not-allowed'
                  : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                  }`}
              >
                ← Anterior
              </button>

              {/* Números das páginas */}
              <div className="flex items-center justify-center space-x-1 flex-wrap">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                  // Mostrar apenas algumas páginas para não sobrecarregar a interface
                  if (
                    page === 1 ||
                    page === totalPages ||
                    (page >= pagination.currentPage - 1 && page <= pagination.currentPage + 1)
                  ) {
                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium rounded-lg border min-w-[2.5rem] sm:min-w-[2.75rem] ${page === pagination.currentPage
                          ? 'bg-primary-600 dark:bg-primary-500 text-white border-primary-600 dark:border-primary-500'
                          : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                          }`}
                      >
                        {page}
                      </button>
                    );
                  } else if (
                    page === pagination.currentPage - 2 ||
                    page === pagination.currentPage + 2
                  ) {
                    return <span key={page} className="px-1 sm:px-2 text-gray-400 dark:text-gray-500 text-xs">...</span>;
                  }
                  return null;
                })}
              </div>

              {/* Botão Próximo */}
              <button
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={pagination.currentPage === totalPages}
                className={`px-3 py-2 text-xs sm:text-sm font-medium rounded-lg border w-full sm:w-auto ${pagination.currentPage === totalPages
                  ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 border-gray-200 dark:border-gray-600 cursor-not-allowed'
                  : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                  }`}
              >
                Próximo →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para Criar/Editar Assunto */}
      {modalData.isOpen && (
        <Modal
          isOpen={modalData.isOpen}
          onClose={closeModal}
          title={modalData.mode === 'create' ? 'Novo Assunto' : 'Editar Assunto'}
          maxWidth="md"
          closeOnBackdropClick={!isSubmitting}
        >

            {/* Formulário */}
            <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-3 sm:space-y-4">
              {/* Nome */}
              <div>
                <label htmlFor="nome" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                  Nome *
                </label>
                <input
                  type="text"
                  id="nome"
                  name="nome"
                  value={formData.nome}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500 dark:focus:border-primary-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  placeholder="Digite o nome do assunto"
                  required
                />
              </div>

              {/* Descrição */}
              <div>
                <label htmlFor="descricao" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                  Descrição *
                </label>
                <textarea
                  id="descricao"
                  name="descricao"
                  value={formData.descricao}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500 dark:focus:border-primary-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  placeholder="Digite a descrição do assunto"
                  required
                />
              </div>

              {/* Departamento */}
              <div>
                <label htmlFor="departamento_id" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                  Departamento *
                </label>
                <select
                  id="departamento_id"
                  name="departamento_id"
                  value={formData.departamento_id}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500 dark:focus:border-primary-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  required
                  disabled={usuario?.nivel === 'admin'}
                >
                  <option value="">Selecione um departamento</option>
                  {departments.map(department => (
                    <option key={department.id} value={department.id}>
                      {department.nome}
                    </option>
                  ))}
                </select>
                {usuario?.nivel === 'admin' && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Você só pode criar/editar assuntos para o seu departamento
                  </p>
                )}
              </div>

              {/* Botões */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-3 pt-3 sm:pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-gray-800"
                  disabled={isSubmitting}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 dark:bg-primary-500 border border-transparent rounded-lg hover:bg-primary-700 dark:hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Salvando...
                    </div>
                  ) : (
                    modalData.mode === 'create' ? 'Criar' : 'Salvar'
                  )}
                </button>
              </div>
            </form>
        </Modal>
      )}

      {/* Modal de Confirmação de Exclusão */}
      {deleteModal.isOpen && (
        <ConfirmationModal
          isOpen={deleteModal.isOpen}
          onClose={closeDeleteModal}
          onConfirm={confirmDelete}
          title="Confirmar Exclusão"
          message={`Tem certeza que deseja excluir o assunto "${deleteModal.subjectName}"? Esta ação não pode ser desfeita.`}
          confirmText="Excluir"
          cancelText="Cancelar"
        />
      )}
    </div>
  );
};

export default Subjects;
